import JsonLd from "@/components/JsonLd";
import { AB_PIN_DETAIL_FULLSCREEN } from "@/constants/ABMng";
import { pagePinDetailFlags } from "@/context/flags/precomputedRoutes";
import { getPin, getRelatedBoards } from "@/services";
import { getSeoInlinks } from "@/services/qingzhou";
import useABTest from "@/utils/hooks/useABtest";
import imageUrl from "@/utils/imageUrl";
import { isBanSSR } from "@/utils/server/checkSSR";
import { boardFilter } from "@/utils/server/dataFilter";
import { isLogined } from "@/utils/server/isLogined";
import { serverSideNotFound } from "@/utils/server/serverSide";
import strOverflow from "@/utils/strOverflow";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useRef } from "react";
import styles from "./index.module.less";

const PinDetail = dynamic(() => import("../../components/PinDetail/index"), {
  ssr: true,
});

const PinDetailFull = dynamic(
  () => import("../../components/PinDetail/index@full"),
  { ssr: true }
);

export default function Pin({
  pin,
  relatedBoards,
  similarMaterials,
  flagValue, // 从服务端传递的AB实验值
} = {}) {
  const router = useRouter();
  const { pinId } = router.query;
  const currentSrcRef = useRef(null);

  // 这里先取一下缓存图，如果有的话，先显示缓存图
  if (typeof window !== "undefined") {
    currentSrcRef.current = window._pinDetailCurrentSrc;
    window._pinDetailCurrentSrc = null;
  }

  // 始终调用useABTest hook以避免条件调用错误
  const clientABTest = useABTest(AB_PIN_DETAIL_FULLSCREEN);
  // const isFull = useABTest(AB_PIN_DETAIL_FULLSCREEN);

  // 使用服务端传递的AB实验值，如果没有则回退到客户端获取
  const isFull =
    flagValue?.hbPinDetailFeedFlag !== undefined
      ? flagValue.hbPinDetailFeedFlag
      : clientABTest?.new;

  const PinDetailComponent = isFull ? PinDetailFull : PinDetail;
  return (
    <div className={styles.pin}>
      <JsonLd
        data={{
          "@id": `https://huaban.com/pins/${pinId}`,
          title: `${pin?.raw_text}图片_${pin?.board?.title}图片素材-花瓣网`,
          description: `${strOverflow(pin?.raw_text, 40)}图片是${
            pin?.user?.username
          }的${pin?.board?.title}画板中的相关图片之一的详情图，${
            pin?.board?.title
          }画板共有${
            pin?.board?.pin_count
          }张设计师收藏采集的相关图片素材资源。花瓣网, 设计师寻找灵感的天堂！`,
          images: [imageUrl(pin?.file, { width: 240 })],
          pubDate:
            pin?.created_at && new Date(pin?.created_at * 1000).toISOString(),
        }}
      />
      <div className={styles.bg}></div>
      <PinDetailComponent
        pinId={pinId}
        pin={pin}
        currentSrc={currentSrcRef.current}
        relatedBoards={relatedBoards}
        similarMaterials={similarMaterials}
        flagValue={flagValue} // 传递服务端AB实验值
      />
    </div>
  );
}

export async function getServerSideProps(context) {
  const { params, req, res } = context;
  const { pinId } = params;
  // 暂时不走客户端获取
  if (req.url.includes(".json")) {
    return {
      props: {},
    };
  }
  // 获取AB实验值
  let flagValue = null;
  try {
    // 直接调用flag函数获取AB实验值
    const hbPinDetailFeedValue =
      await pagePinDetailFlags.flagMap.hbPinDetailFeedFlag(req);
    const similarMaterialListValue =
      await pagePinDetailFlags.flagMap.similarMaterialListFlag(req);

    flagValue = {
      hbPinDetailFeedFlag: hbPinDetailFeedValue,
      similarMaterialListFlag: similarMaterialListValue,
    };
  } catch (error) {
    console.error("Failed to process flags:", error);
  }

  // 已登录用户不使用SSR
  // 客户端请求不走服务端请求
  if (isLogined(req) || req.url.includes(".json")) {
    return {
      props: {
        flagValue,
      },
    };
  }

  // 判断是否在服务端渲染
  const renderInClient = await isBanSSR(context);
  if (renderInClient) {
    return {
      props: {},
    };
  }

  if (Number.isNaN(+pinId)) {
    return serverSideNotFound(res);
  }

  const result = await getPin({
    pin_id: pinId,
    pins: 20,
    ignoreMessage: true,
    related_topic: "material",
  });

  if (result.err === 404) {
    return serverSideNotFound(res);
  }

  const inlinks = await getSeoInlinks({
    title: `${result.pin?.raw_text}图片_${result.pin?.board?.title}图片素材-花瓣网`,
    current_url: req.url,
  });

  const relatedBoards = await getRelatedBoards({
    pin_id: pinId,
    max: 20,
    limit: 20,
  });

  return {
    props: {
      pin: result.pin,
      relatedBoards: boardFilter(relatedBoards),
      inlinks,
      flagValue, // 传递AB实验值到客户端
    },
  };
}
